import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';

export const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name || !formData.company || !formData.email || !formData.message) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.name.trim().length < 2) {
      setError('Le nom doit contenir au moins 2 caractères');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    if (formData.message.trim().length < 10) {
      setError('Le message doit contenir au moins 10 caractères');
      return;
    }

    setIsSubmitting(true);

    try {
      // Créer FormData pour Formspree
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name.trim());
      formDataToSubmit.append('email', formData.email.trim());
      formDataToSubmit.append('company', formData.company.trim());
      formDataToSubmit.append('message', formData.message.trim());
      
      // Soumettre à Formspree
      const response = await fetch('https://formspree.io/f/xgopynek', {
        method: 'POST',
        body: formDataToSubmit,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        const data = await response.json();
        setError(data.error || 'Une erreur est survenue lors de l\'envoi');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="cta-section" className="cta-section py-32 bg-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="abstract-shape shape-6"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto fade-in-section">
          <div className="text-center mb-16">
            <h2 className="section-title mb-6">
              Vérifions d'abord si le problème<br />
              <span className="text-accent">existe réellement</span>.
            </h2>
            <p className="section-intro max-w-2xl mx-auto">
              Décrivez brièvement votre entreprise, l'offre que vous souhaitez développer et ce qui vous semble aujourd'hui difficile à faire comprendre.<br /><br />
              Ce premier échange permettra de déterminer si un Gap de Perception est pertinent dans votre situation.
            </p>
          </div>

          {isSubmitted ? (
            <div className="success-message text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-green-100 rounded-full">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Message envoyé avec succès</h3>
              <p className="text-gray-600 mb-6">Je reviendrai vers vous rapidement pour poursuivre la discussion.</p>
              <Button 
                onClick={() => setIsSubmitted(false)}
                variant="outline"
                className="mt-4"
              >
                Envoyer un autre message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="cta-form bg-gray-50 p-12 rounded-lg border border-gray-200">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-6">
                <div className="form-group">
                  <Label htmlFor="name" className="form-label">
                    Votre nom *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jean Dupont"
                    className="form-input"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="company" className="form-label">
                    Votre entreprise *
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Nom de l'entreprise"
                    className="form-input"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    Votre email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jean.dupont@entreprise.com"
                    className="form-input"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="message" className="form-label">
                    Votre message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez brièvement votre situation et vos objectifs..."
                    className="form-input min-h-[120px] resize-none"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="cta-button w-full group"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span>
                      Envoi en cours...
                    </span>
                  ) : (
                    <>
                      Clarifier ma position maintenant
                      <Send className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Échange confidentiel • Réponse sous 48h
                </p>
              </div>
            </form>
          )}

          <div className="mt-20 pt-16 border-t border-gray-200 text-center">
            <p className="final-statement text-xl text-gray-700 leading-relaxed font-semibold">
              Une marque forte n'est pas un luxe.<br />
              C'est un <span className="text-accent">avantage concurrentiel décisif</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
